import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type {
  PartnerPreRegistrationSummary,
  PartnerPreRegistrationsApiResponse,
  UseGetPartnerPreRegistrationsReturn,
  UseGetPartnerPreRegistrationsParams,
} from "../types"
import { PaginationInfo } from "src/components/tailwind-components/Pagination"

export const PARTNER_PRE_REGISTRATION_LIST_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/partner_pre_registrations`
/**
 * パートナー事前登録一覧を取得するカスタムフック
 *
 * @param params - 取得パラメータ
 * @param params.page - ページ番号（デフォルト: 1）
 * @returns パートナー事前登録一覧とその操作メソッド
 *
 */
export const useGetPartnerPreRegistrationList = (
  params: UseGetPartnerPreRegistrationsParams = {}
): UseGetPartnerPreRegistrationsReturn => {
  const { getWithToken } = useSmoothApi()

  // URLにクエリパラメータを追加
  const searchParams = new URLSearchParams()

  if (params.page) {
    searchParams.append("page", params.page.toString())
  }
  if (params.statuses) {
    searchParams.append("statuses", params.statuses.join(","))
  }

  const url = searchParams.toString()
    ? `${PARTNER_PRE_REGISTRATION_LIST_URL}?${searchParams.toString()}`
    : PARTNER_PRE_REGISTRATION_LIST_URL

  // SWRキーを生成（パラメータを含む）
  const swrKey = [url, params]
  /**
   * データ取得用のfetcher関数
   * @returns APIレスポンス
   */
  const fetcher = async (): Promise<PartnerPreRegistrationsApiResponse> => {
    try {
      const response = await getWithToken(url)
      return response.data as PartnerPreRegistrationsApiResponse
    } catch (error) {
      // エラーログ出力（開発環境での診断用）
      console.error("パートナー事前登録一覧の取得に失敗しました:", error)
      throw error
    }
  }

  // SWRでデータフェッチング
  const swr = useSWR(swrKey, fetcher, {
    // 再検証の設定
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    // エラー時の再試行設定
    errorRetryCount: 3,
    errorRetryInterval: 1000,
  })

  // データの抽出と型安全性の確保
  const partnerPreRegistrations: PartnerPreRegistrationSummary[] =
    swr.data?.partnerPreRegistrations || []
  const pagination: PaginationInfo | undefined = swr.data?.pagination
  const isLoading = !swr.data && !swr.error
  const error = swr.error
  const isEmpty = !isLoading && partnerPreRegistrations.length === 0

  /**
   * データを強制的にリフレッシュする関数
   * キャッシュをクリアして新しいデータを取得
   */
  const refresh = async () => {
    return swr.mutate(undefined, { revalidate: true })
  }

  return {
    // データ
    partnerPreRegistrations,
    pagination,
    data: swr.data,

    // 状態
    isLoading,
    error,
    isEmpty,

    // 操作メソッド
    mutate: swr.mutate,
    refresh,
  }
}
